import { Form, Skeleton, Tabs, type TabsProps } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type BaseOptionType } from "antd/lib/select"
import { type ValidateErrorEntity } from "rc-field-form/es/interface"
import { useEffect, useMemo } from "react"

import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { fetchLeadsFilterOptions } from "../../../../../Leads/redux/leads.thunks.ts"
import { type ProjectFormData } from "../../project-form-data.ts"

interface MilestonesProjectTypesProps {
  form: FormInstance<ProjectFormData>
}

export function MilestonesProjectTypes({ form }: MilestonesProjectTypesProps) {
  const activeId = Form.useWatch("activeProjectTypeId", form)?.toString() || ""
  const selectedIds = Form.useWatch("project_type", form)

  const dispatch = useAppDispatch()
  const fetching = useAppSelector((state) => state.leads.fetchingFilterOptions)
  const filterOptions = useAppSelector((state) => state.leads.filterOptions)

  const projectTypeMap: Map<string, string> = useMemo(
    () =>
      new Map(
        filterOptions?.project_type?.map((p: BaseOptionType) => [
          p.value,
          p.label
        ])
      ),
    [filterOptions?.project_type]
  )

  const selectedProjectTypes: TabsProps["items"] = useMemo(
    () =>
      selectedIds?.map((id) => {
        const projectType = projectTypeMap.get(id)

        return {
          key: id,
          label: projectType
        }
      }),
    [selectedIds, projectTypeMap]
  )

  useEffect(() => {
    dispatch(fetchLeadsFilterOptions())
  }, [dispatch])

  if (fetching) {
    return <Skeleton.Node className={"h-14 w-full"} />
  }

  if (!selectedProjectTypes?.length) {
    return (
      <button
        type={"button"}
        onClick={() =>
          form.scrollToField("project_type", {
            scrollMode: "always",
            behavior: "smooth",
            block: "center"
          })
        }
        className={
          "h-9 bg-primary/30 w-full cursor-pointer text-bold rounded-t-card flex justify-center items-center text-center"
        }
      >
        Please select project type
      </button>
    )
  }

  const onChange = (key: string) => {
    form
      .validateFields(["project_phases"], { validateOnly: false })
      .then((e) => form.setFieldValue("activeProjectTypeId", key))
      .catch((e: ValidateErrorEntity<ProjectFormData>) => {})
  }

  return (
    <Tabs
      activeKey={activeId}
      className={"h-14"}
      size={"small"}
      items={selectedProjectTypes}
      onChange={onChange}
    />
  )
}
